import { useEffect } from "react";
import { useLocation, useNavigationType } from "react-router-dom";

/**
 * Rolagem entre telas.
 *
 * Ir para uma tela nova: começa do topo. Voltar (POP): volta para onde a
 * pessoa estava — e é aqui que o app falhava. Três avaliações dizem a mesma
 * coisa: "when I go look at one and I close it it takes me back to the
 * beginning", "you have to start from the beginning of the app, not where
 * you had just been". O navegador tenta restaurar sozinho, mas numa SPA a
 * lista ainda não existe no momento em que ele tenta: a página está curta,
 * a rolagem bate em zero e fica lá.
 *
 * Então a restauração é nossa: a posição de cada entrada do histórico fica
 * em sessionStorage (chave = location.key), e ao voltar a gente espera a
 * página ter altura suficiente antes de rolar, tentando a cada frame por até
 * ~2 s. `history.scrollRestoration = "manual"` tira o navegador do caminho.
 */
const PREFIXO = "glucofy-rolagem:";
const TENTATIVAS_MAX = 120; // ~2 s a 60 fps

function ler(chave: string): number | null {
  try {
    const v = window.sessionStorage.getItem(PREFIXO + chave);
    return v == null ? null : Number(v);
  } catch {
    return null;
  }
}

function gravar(chave: string, y: number) {
  try {
    window.sessionStorage.setItem(PREFIXO + chave, String(Math.round(y)));
  } catch {
    /* sem armazenamento: perde a posição, não quebra */
  }
}

const ScrollToTop = () => {
  const { pathname, key } = useLocation();
  const navType = useNavigationType();

  useEffect(() => {
    if ("scrollRestoration" in window.history) window.history.scrollRestoration = "manual";
  }, []);

  // Grava a posição desta entrada do histórico enquanto a pessoa rola.
  //
  // O valor gravado é o lido NO EVENTO de rolagem, nunca o lido no
  // desmontar: quando o cleanup roda, o React já trocou a tela, a página
  // encolheu e window.scrollY vem cortado (medido na beta: 1400 virava 383).
  useEffect(() => {
    let agendado = 0;
    let ultimoY: number | null = null;
    const aoRolar = () => {
      ultimoY = window.scrollY;
      if (agendado) return;
      agendado = window.requestAnimationFrame(() => {
        agendado = 0;
        if (ultimoY != null) gravar(key, ultimoY);
      });
    };
    window.addEventListener("scroll", aoRolar, { passive: true });
    return () => {
      window.removeEventListener("scroll", aoRolar);
      if (agendado) {
        window.cancelAnimationFrame(agendado);
        if (ultimoY != null) gravar(key, ultimoY);
      }
    };
  }, [key]);

  useEffect(() => {
    if (navType !== "POP") {
      window.scrollTo({ top: 0, left: 0, behavior: "instant" });
      return;
    }
    const alvo = ler(key);
    if (alvo == null || alvo <= 0) return;

    let tentativas = 0;
    let raf = 0;
    const tentar = () => {
      const alturaMax = document.documentElement.scrollHeight - window.innerHeight;
      if (alturaMax >= alvo || tentativas >= TENTATIVAS_MAX) {
        window.scrollTo({ top: Math.min(alvo, Math.max(alturaMax, 0)), left: 0, behavior: "instant" });
        return;
      }
      tentativas += 1;
      raf = window.requestAnimationFrame(tentar);
    };
    raf = window.requestAnimationFrame(tentar);
    return () => window.cancelAnimationFrame(raf);
  }, [pathname, key, navType]);

  return null;
};

export default ScrollToTop;
